import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import { isLoading } from "../../Loading";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import ErrorScaffold from "../ErrorScaffold";
import LoadingScaffold from "../LoadingScaffold";
import CareMomentsScreen from "./CareMomentsScreen";

interface Props {
  api: API;
  appState: AppState;
  onClickGoBack: () => void;
  onRequestNavigate: (page: Page) => void;
}

export default function CareMomentsActivity(props: Props) {
  const query = useQuery({
    queryKey: ["care_moments"],
    staleTime: 0,
    queryFn: () => props.api.getCaregiverRankOverview(),
    onSuccess: () => {
      markAsViewed.mutate();
    },
    select: (data) => {
      const ranks = data.ranks.map((rank) => ({
        ...rank,
        wage: +(rank.wage / 100).toFixed(2),
      }));
      const toRank = ranks.find((rank) => rank.id === data.rank.to.id);
      const fromRank =
        data.rank.from === null ? toRank : ranks.find((rank) => rank.id === data.rank.from?.id);

      if (toRank === undefined || fromRank === undefined) {
        throw new Error("Rank not found");
      }

      return {
        rank: {
          from: {
            rank: fromRank,
            nextRankPercents: data.rank.from?.nextRankPercents ?? data.rank.to.nextRankPercents,
          },
          to: {
            rank: toRank,
            nextRankPercents: data.rank.to.nextRankPercents,
            points: data.rank.to.points,
          },
        },
        ranks: ranks,
      };
    },
  });

  const markAsViewed = useMutation({
    mutationFn: () => props.api.markCaregiverRankAdjustmentsViewed(),
  });

  const caregiver = useCaregiver(props.appState);

  if (isLoading(caregiver) || query.status === "loading") {
    return (
      <LoadingScaffold
        title={translate(STRINGS.CARE_MOMENTS)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  switch (query.status) {
    case "error":
      return (
        <ErrorScaffold
          title={translate(STRINGS.CARE_MOMENTS)}
          onClickGoBack={props.onClickGoBack}
          message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        />
      );

    case "success":
      return (
        <CareMomentsScreen
          rankChange={query.data.rank}
          ranks={query.data.ranks}
          onClickGoBack={props.onClickGoBack}
          caregiverAvatarUrl={caregiver.photoUrl}
          onRequestNavigate={props.onRequestNavigate}
        />
      );
  }
}
